import {
  APP_PAGE_TYPE,
  usePageVisitTracking,
} from '../../hooks/usePageVisitTracking';
import DashboardPage from '../DashboardPage';
import { GLOBAL_AI_ASSISTANT_GREETING } from '../global-ai-assistant/global-ai-assistant-copy';
import { GlobalAiChatInput } from '../global-ai-assistant/global-ai-chat-input';
import { GlobalAiChatTranscriptPane } from '../global-ai-assistant/global-ai-chat-transcript-pane';
import {
  GlobalAiConversationSidebar,
  GlobalAiConversationSidebarToggle,
  useConversationSidebarCollapsed,
} from '../global-ai-assistant/global-ai-conversation-sidebar';
import styles from './HomepageChat.module.css';
import layoutStyles from './HomepageChatLayout.module.css';

/**
 * The homepage once a conversation is under way (`Homepage` switches to it):
 * the shared transcript pane inline — the same conversation the floating
 * drawer shows on every other page; the host suppresses the drawer here.
 * Figma: Dashboard 2026, 437-26845 (dark) / 437-27359 (light).
 */
export function HomepageChat() {
  const [sidebarCollapsed, toggleSidebarCollapsed] =
    useConversationSidebarCollapsed();

  // Analytics knew this page as the landing page; renaming it would split
  // its visit history.
  usePageVisitTracking({
    page: 'Landing',
    pageId: 'landing',
    pageType: APP_PAGE_TYPE,
  });

  return (
    // "AI Chat" IS the page title (no welcome greeting) and the
    // history-sidebar toggle rides next to it in the title row.
    <DashboardPage
      title={['AI Chat']}
      titleSuffix={
        sidebarCollapsed ? (
          <GlobalAiConversationSidebarToggle
            collapsed
            onToggle={toggleSidebarCollapsed}
          />
        ) : (
          // Open state: the toggle right-aligns with the sidebar's content
          // edge (absolute against the page container, anchored via the
          // :has rule in the module css).
          <div className={styles.sidebarToggleAligned}>
            <GlobalAiConversationSidebarToggle
              collapsed={false}
              onToggle={toggleSidebarCollapsed}
            />
          </div>
        )
      }
      loading={false}
    >
      <div className={layoutStyles.layout}>
        {/* Always mounted: the sidebar animates its own width shut when
            collapsed (snappy nav-style snap) instead of unmount-popping. */}
        <GlobalAiConversationSidebar collapsed={sidebarCollapsed} />
        <div className={layoutStyles.main} data-testid="assistant-homepage">
          {/* Full-width column so the transcript's scrollbar lands at the page
              edge (ChatGPT-style), not down the middle of a narrow box. The
              pane centers its messages to 576px internally; the pinned input
              is re-centered to match. */}
          <div className={layoutStyles.chatPane}>
            <GlobalAiChatTranscriptPane variant="homepage" />
            <div className={layoutStyles.inputDock}>
              <div className={layoutStyles.inputColumn}>
                <GlobalAiChatInput
                  testId="assistant-home-prompt"
                  placeholder={GLOBAL_AI_ASSISTANT_GREETING}
                  wrapClassName={styles.homeInputWrap}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardPage>
  );
}
